import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Fade,
  Grow,
} from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import ViewKanbanRoundedIcon from '@mui/icons-material/ViewKanbanRounded';
import dayjs from 'dayjs';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import { azureExportExport } from './functions/azure_export_export.function';
import { handleApiError } from '../../common/functions/errorHandler.function';

const AZURE_BOARD_URL = 'https://dev.azure.com/softoo-slk/Jazz%20Support';

export const JazzSupportPage = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [mounted, setMounted] = useState(false);

  const permissionNames = useMemo(() => {
    try {
      const stored = localStorage.getItem('permissions');
      if (!stored) {
        return [];
      }
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map((p) => p.name) : [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    if (!permissionNames.includes('Jazz Support')) {
      navigate('/rov', { replace: true });
    }
  }, [permissionNames, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end date.');
      return;
    }
    if (dayjs(startDate).isAfter(dayjs(endDate))) {
      setError('Start date must be before or equal to end date.');
      return;
    }
    setError(null);
    setInfoMessage(null);
    setLoading(true);
    try {
      const { blob, isJson } = await azureExportExport(startDate, endDate);
      if (isJson) {
        const text = await blob.text();
        const json = JSON.parse(text);
        setInfoMessage(json.message || 'No issues found for the given date range.');
        setError(null);
        return;
      }
      setInfoMessage(null);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `issues_${startDate}_to_${endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setInfoMessage(null);
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          setError(json.message || json.error || 'Export failed. Please try again.');
        } catch {
          setError('Export failed. Please try again.');
        }
      } else {
        handleApiError(err);
        setError(err?.response?.data?.message || 'Export failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!permissionNames.includes('Jazz Support')) {
    return null;
  }

  return (
    <DashboardLayout padding={true}>
      <Box
        sx={{
          maxWidth: '92%',
          mx: 'auto',
          mt: 2,
          mb: 4,
          px: { xs: 2, sm: 0 },
        }}
      >
        {/* Hero header with gradient and animation */}
        <Fade in={mounted} timeout={400}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #192126 0%, #63a7d1 50%, #1a2529 100%)',
              borderRadius: 3,
              p: 3,
              mb: 3,
              boxShadow: '0 12px 40px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.12)',
              border: '1px solid rgba(255,255,255,0.06)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #BD1D23, #e63946)',
                opacity: 0.9,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  background: 'linear-gradient(145deg, rgba(189,29,35,0.25), rgba(189,29,35,0.1))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(189,29,35,0.4)',
                }}
              >
                <DashboardRoundedIcon sx={{ fontSize: 32, color: '#e63946' }} />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: '#fff',
                    letterSpacing: '-0.02em',
                    mb: 0.5,
                  }}
                >
                  Jazz Support
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem' }}
                >
                  Manage issues and export state history from Azure DevOps
                </Typography>
              </Box>
            </Box>
          </Box>
        </Fade>

        {/* Open Board card */}
        <Grow in={mounted} timeout={400} style={{ transformOrigin: 'center top' }}>
          <Paper
            elevation={0}
            sx={{
              p: 0,
              mb: 3,
              borderRadius: 2.5,
              border: '1px solid rgba(0,120,212,0.12)',
              backgroundColor: '#fff',
              boxShadow: '0 4px 24px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
              transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                background: 'linear-gradient(180deg, #0078d4 0%, #005a9e 100%)',
                opacity: 0.9,
              },
              '&:hover': {
                boxShadow: '0 12px 40px rgba(0,120,212,0.12), 0 4px 12px rgba(0,0,0,0.08)',
                borderColor: 'rgba(0,120,212,0.25)',
                '& .open-board-icon': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 8px 24px rgba(0,120,212,0.25)',
                },
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2.5,
                p: 3,
                pl: { xs: 3, sm: 4 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  flex: '1 1 auto',
                  minWidth: 0,
                }}
              >
                <Box
                  className="open-board-icon"
                  sx={{
                    flexShrink: 0,
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    background: 'linear-gradient(145deg, #0078d4 0%, #005a9e 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 6px 16px rgba(0,120,212,0.35)',
                    transition: 'transform 0.35s ease, box-shadow 0.35s ease',
                  }}
                >
                  <ViewKanbanRoundedIcon sx={{ fontSize: 28, color: '#fff' }} />
                </Box>
                <Box sx={{ minWidth: 0, pt: 0.25 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#192126',
                      letterSpacing: '-0.01em',
                      mb: 0.75,
                      lineHeight: 1.3,
                    }}
                  >
                    Open Board
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.55,
                      fontSize: '0.9375rem',
                      maxWidth: 420,
                    }}
                  >
                    View and manage Jazz Support work items directly in Azure DevOps Boards.
                  </Typography>
                </Box>
              </Box>
              <Button
                component="a"
                href={AZURE_BOARD_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="large"
                startIcon={<OpenInNewRoundedIcon />}
                sx={{
                  flexShrink: 0,
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '0.9375rem',
                  borderRadius: 2,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #0078d4 0%, #005a9e 100%)',
                  boxShadow: '0 4px 14px rgba(0,120,212,0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #106ebe 0%, #004578 100%)',
                    boxShadow: '0 6px 22px rgba(0,120,212,0.5)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                Open in Azure
              </Button>
            </Box>
          </Paper>
        </Grow>

        {/* Export card with animation */}
        <Grow in={mounted} timeout={500} style={{ transformOrigin: 'center top' }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid #E8E9E9',
              backgroundColor: '#fff',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                borderColor: '#BD1D23',
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#192126', mb: 1.5 }}>
              Export to Excel
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a date range and download issue state history as an Excel file.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Start date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value || '')}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: 'grey.50',
                    '&:hover': { backgroundColor: 'grey.100' },
                    '&.Mui-focused': { backgroundColor: '#fff' },
                  },
                }}
              />
              <TextField
                label="End date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value || '')}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: 'grey.50',
                    '&:hover': { backgroundColor: 'grey.100' },
                    '&.Mui-focused': { backgroundColor: '#fff' },
                  },
                }}
              />
              {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 1.5 }}>
                  {error}
                </Alert>
              )}
              {infoMessage && (
                <Alert
                  severity="info"
                  onClose={() => setInfoMessage(null)}
                  sx={{ borderRadius: 1.5 }}
                >
                  {infoMessage}
                </Alert>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={handleExport}
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <DownloadRoundedIcon />
                }
                sx={{
                  mt: 0.5,
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #BD1D23 0%, #9a191e 100%)',
                  boxShadow: '0 4px 14px rgba(189,29,35,0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #c92a30 0%, #BD1D23 100%)',
                    boxShadow: '0 6px 20px rgba(189,29,35,0.45)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                {loading ? 'Exporting...' : 'Download Excel'}
              </Button>
            </Box>
          </Paper>
        </Grow>
      </Box>
    </DashboardLayout>
  );
};
